<template>
  <div class="gauge" :class="{ 'gauge--fill': fill }" :title="title">
    <svg class="gauge-svg" viewBox="0 0 200 124" preserveAspectRatio="xMidYMid meet">
      <path :d="arc(0, 100)" class="gauge-track" />
      <path :d="arc(0, percent)" class="gauge-value" :style="{ stroke: arcColor, filter: 'drop-shadow(0 0 6px rgba(' + rgb + ', 0.35))' }" />
      <text x="100" y="86" text-anchor="middle" class="gauge-number">{{ percentLabel }}</text>
      <text x="100" y="103" text-anchor="middle" class="gauge-caption">{{ caption }}</text>
      <text v-if="hasParts" x="100" y="118" text-anchor="middle" class="gauge-parts">{{ formatNumber(parts.booked) }} of {{ formatNumber(parts.total) }} {{ unit }}</text>
    </svg>
  </div>
</template>

<script>
export default {
  name: 'GaugeChart',
  props: {
    // One row from a ratio branch: { value (percent), booked, total }
    chartData: {
      type: Array,
      required: true,
    },
    caption: {
      type: String,
      default: '',
    },
    unit: {
      type: String,
      default: '',
    },
    fill: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: '',
    },
  },
  computed: {
    rgb() {
      // Widget highlight color as "r, g, b" for rgba() mixes; falls back to the brand primary.
      const hex = /^#[0-9a-fA-F]{6}$/.test(this.color || '') ? this.color : '#e14eca';
      return [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16)).join(', ');
    },
    row() {
      return this.chartData && this.chartData.length > 0 ? this.chartData[0] : null;
    },
    percent() {
      const v = this.row ? Number(this.row.value) : 0;
      return Math.max(0, Math.min(100, isNaN(v) ? 0 : v));
    },
    percentLabel() {
      return this.row && this.row.value !== null && this.row.value !== undefined ? Math.round(this.percent) + '%' : 'n/a';
    },
    parts() {
      return this.row ? { booked: Number(this.row.booked) || 0, total: Number(this.row.total) || 0 } : { booked: 0, total: 0 };
    },
    hasParts() {
      return this.row && this.row.total !== undefined && this.row.total !== null;
    },
    arcColor() {
      return 'rgb(' + this.rgb + ')';
    },
    title() {
      return this.hasParts ? this.percentLabel + ' (' + this.parts.booked + ' of ' + this.parts.total + ' ' + this.unit + ')' : this.percentLabel;
    },
  },
  methods: {
    formatNumber(n) {
      const group = JSON.parse(localStorage.getItem('group'));
      const locale = group && group.default_locale ? group.default_locale : undefined;
      return new Intl.NumberFormat(locale).format(n);
    },
    arc(fromPct, toPct) {
      // Semicircle from 180deg to 0deg, radius 80 around (100, 100).
      const toXY = (pct) => {
        const angle = Math.PI - (pct / 100) * Math.PI;
        return [100 + 80 * Math.cos(angle), 100 - 80 * Math.sin(angle)];
      };
      const [x1, y1] = toXY(fromPct);
      const [x2, y2] = toXY(Math.max(fromPct + 0.01, toPct));
      const large = toPct - fromPct > 50 ? 1 : 0;
      return 'M ' + x1.toFixed(2) + ' ' + y1.toFixed(2) + ' A 80 80 0 ' + large + ' 1 ' + x2.toFixed(2) + ' ' + y2.toFixed(2);
    },
  },
};
</script>

<style scoped>
.gauge {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.gauge--fill {
  height: 100%;
  min-height: 0;
}
/* Scales with the tile in both directions; the arc and its text share one viewBox so they stay proportional. */
.gauge-svg {
  width: 100%;
  height: 100%;
  min-height: 0;
  max-width: 520px;
}
.gauge-track {
  fill: none;
  stroke: rgba(125, 125, 125, 0.28);
  stroke-width: 20;
  stroke-linecap: round;
}
.gauge-value {
  fill: none;
  stroke-width: 20;
  stroke-linecap: round;
  transition: d 0.3s ease;
}
.gauge-number {
  font-size: 38px;
  font-weight: 700;
  fill: #ffffff;
}
.gauge-caption {
  font-size: 11px;
  font-weight: 500;
  fill: rgba(255, 255, 255, 0.8);
}
.gauge-parts {
  font-size: 10px;
  fill: rgba(255, 255, 255, 0.65);
}
.white-content .gauge-number {
  fill: #1e1e2f;
}
.white-content .gauge-caption {
  fill: rgba(0, 0, 0, 0.75);
}
.white-content .gauge-parts {
  fill: rgba(0, 0, 0, 0.6);
}
</style>
